import type { ChangeEvent, DragEvent } from 'react';

import { ErrorReporter } from 'js/src/libs/error-reporter';

export type AugmentedFile = File & {
    relativePath?: string;
    mimeTypeHint?: string;
};

type FileSystemEntryWithTypeHint = {
    entry: FileSystemEntry;
    mimeTypeHint: string;
};

/*
We found some files (like the one attached to this ticket: https://lumen5.atlassian.net/browse/IO-1799)
where retrieving the File from the drag-and-drop event produces a file with a file.type of "" rather than
a valid mime type. However, the mime types that are supported by the browser are available via a different way
(through the dataTransferItem.type property).

To handle this, we add a new property to the File object called mimeTypeHint, which can be used as a fallback
if the file.type property is empty.

--- Note ---
* If the file is of a type that the browser doesn't support (like mkv), the mimeTypeHint is populated
  in addMimeTypeHintToFiles. (see https://github.com/Lumen5/web/pull/17273)

* Other than those types, if the file is selected through the file dialog, the file.type
  property is always populated correctly (type is only "" when the file is dragged-and-dropped).

There is a similar chrome bug report for this issue here (though that one is specifically for MS Office documents):

https://bugs.chromium.org/p/chromium/issues/detail?id=155455
*/
export const getFileMimeType = (file: AugmentedFile) => {
    if (file.type) {
        return file.type;
    } else if (file.mimeTypeHint) {
        return file.mimeTypeHint;
    } else {
        return '';
    }
};

class NativeFileHandler {
    /*
        NOTE: most of the code for this component comes from:
            https://github.com/blueimp/jQuery-File-Upload/blob/master/js/jquery.fileupload.js
    */

    static getFilesFromDropEvent(e: DragEvent, callback: (files: AugmentedFile[]) => void) {
        if (e.nativeEvent && e.nativeEvent.dataTransfer) {
            e.dataTransfer = e.nativeEvent.dataTransfer;
        }
        const dataTransfer = e.nativeEvent.dataTransfer;
        if (dataTransfer && dataTransfer.files && dataTransfer.files.length) {
            e.preventDefault();
            NativeFileHandler._getDroppedFiles(dataTransfer).done((files: File[]) => {
                callback(NativeFileHandler.addMimeTypeHintToFiles(files));
            });
        }
    }

    static getFilesFromChangeEvent(e: ChangeEvent, callback: (files: AugmentedFile[]) => void) {
        NativeFileHandler._getFileInputFiles($(e.target as HTMLElement)).done(function (
            files: File[],
        ) {
            callback(NativeFileHandler.addMimeTypeHintToFiles(files));
        });
    }

    /**
     * Aguments the given files with a mimeTypeHint property, if the file has
     * an empty File.type property.
     * @param files Files without a type hint.
     * @returns Files with a mimeTypeHint property for those files with
     * an empty type property.
     */
    static addMimeTypeHintToFiles(files: File[]): AugmentedFile[] {
        return files.map((file: File) => {
            const afile = file as AugmentedFile;
            afile.mimeTypeHint = NativeFileHandler.getMimeFromFileIfUnsupported(afile);
            return afile;
        });
    }

    /**
     * Returns an MIME type hint from the given file name, or undefined if the file
     * already has a supported MIME type. Also returns undefined if the file
     * has an unknown MIME type.
     */
    static getMimeFromFileIfUnsupported(file: File): string | undefined {
        const mimeType = getFileMimeType(file);
        if (mimeType !== '') {
            return mimeType;
        }
        // The file has an MIME type that is unsupported by the browser,
        // we manually parse and return MIME type based on the file extension.
        if (file.name.match(/^.+\.mkv$/i)) {
            // mkv files are only supported by the recently updated browsers
            // since it was approved by IANA at the end of 2023.
            return 'video/x-matroska';
        }
        // This file has an unknown MIME type, we return undefined
        return undefined;
    }

    static _getDroppedFiles(dataTransfer: DataTransfer) {
        dataTransfer = dataTransfer || ({} as DataTransfer);
        const items = dataTransfer.items;
        if (items && items.length) {
            return NativeFileHandler._handleFileTreeEntries(
                Array.from(items).reduce(
                    (acc: FileSystemEntryWithTypeHint[], item: DataTransferItem) => {
                        const entry = item.webkitGetAsEntry();
                        if (entry) {
                            acc.push({
                                entry: item.webkitGetAsEntry(),
                                mimeTypeHint: item.type,
                            } as FileSystemEntryWithTypeHint);
                        }
                        return acc;
                    },
                    [],
                ),
            );
        }

        const data_transfer_files = Array.from(dataTransfer.files);
        return NativeFileHandler._isFileListDirectory(dataTransfer.files)
            .then(function (is_directory) {
                if (is_directory) {
                    alert(
                        'Your browser is incompatible with dragging-and-dropping folders. Try dragging only files here, or try out Google Chrome!',
                    );
                    return [];
                } else {
                    return data_transfer_files;
                }
            })
            .promise();
    }

    static _isFileListDirectory(files: FileList) {
        return $.when(
            ...Array.from(files).map(function (file: File) {
                const dfd = $.Deferred();
                const reader = new FileReader();

                reader.onload = function (e) {
                    dfd.resolve(false);
                };
                reader.onerror = function (e) {
                    dfd.resolve(true);
                };
                reader.readAsText(file);
                return dfd.promise();
            }),
        ).then(function (...args) {
            for (let i = 0; i < args.length; i++) {
                if (args[i] === true) {
                    return true;
                }
            }
            return false;
        });
    }

    static _getFileInputFiles(fileInput: JQuery<HTMLElement> | JQuery<HTMLElement>[]) {
        if (!(fileInput instanceof $) || fileInput.length === 1) {
            return NativeFileHandler._getSingleFileInputFiles(fileInput as JQuery<HTMLElement>);
        }
        return $.when(
            ...$.map(
                fileInput as JQuery<HTMLElement>[],
                NativeFileHandler._getSingleFileInputFiles,
            ),
        ).then(function (...args) {
            return Array.prototype.concat.apply([], args);
        });
    }

    static _getSingleFileInputFiles(fileInput: JQuery<HTMLElement>) {
        fileInput = $(fileInput);
        const entries: FileSystemEntry[] =
            fileInput.prop('webkitEntries') || fileInput.prop('entries');
        if (entries && entries.length) {
            const entriesWithTypeHint = entries.map((entry) => {
                return {
                    entry,
                    mimeTypeHint: '', // a hint is not needed for files from the file input
                };
            });
            return NativeFileHandler._handleFileTreeEntries(entriesWithTypeHint);
        }

        let files = $.makeArray<File>(fileInput.prop('files'));
        let value;
        if (!files.length) {
            value = fileInput.prop('value');
            if (!value) {
                return $.Deferred().resolve([]).promise();
            }
            // If the files property is not available, the browser does not
            // support the File API nd we add a pseudo File object with the
            // input value as name with the path information removed:
            files = [{ name: value.replace(/^.*\\/, '') }] as File[];
        } else if (files[0].name === undefined && (files[0] as any).fileName) {
            // File normalization for Safari 4 and Firefox 3:
            $.each(files, function (index, file: any) {
                file.name = file.fileName;
                file.size = file.fileSize;
            });
        }
        return $.Deferred().resolve(files).promise();
    }

    static _handleFileTreeEntries(
        entriesWithTypeHint: FileSystemEntryWithTypeHint[],
        path: string | undefined = undefined,
    ) {
        return $.when(
            ...entriesWithTypeHint.map(function (entryWithTypeHint: FileSystemEntryWithTypeHint) {
                return NativeFileHandler._handleFileTreeEntry(entryWithTypeHint, path);
            }),
        ).then(function (...args) {
            return Array.prototype.concat.apply([], args);
        });
    }

    static _handleFileTreeEntry(
        entryWithTypeHint: FileSystemEntryWithTypeHint,
        path: string | undefined = undefined,
    ) {
        const dfd = $.Deferred();
        let entries: FileSystemEntry[] = [];

        const errorHandler = function (e: any) {
            if (e && !e.entry) {
                e.entry = entryWithTypeHint.entry;
                // Since $.when returns immediately if one
                // Deferred is rejected, we use resolve instead.
                // This allows valid files and invalid items
                // to be returned together in one set:
                dfd.resolve([e]);
            }
        };
        const successHandler = (entries: FileSystemEntry[]) => {
            const entriesWithTypeHint = entries.map((entry) => {
                return {
                    entry,
                    mimeTypeHint: '', // we cannot provide a hint for files inside of directories (I cannot find an API to do this)
                };
            });
            NativeFileHandler._handleFileTreeEntries(
                entriesWithTypeHint,
                path + entryWithTypeHint.entry.name + '/',
            )
                .done(function (files) {
                    dfd.resolve(files);
                })
                .fail(errorHandler);
        };
        const readEntries = (dirReader: FileSystemDirectoryReader) => {
            dirReader.readEntries(function (results) {
                if (!results.length) {
                    successHandler(entries);
                } else {
                    entries = entries.concat(results);
                    readEntries(dirReader);
                }
            }, errorHandler);
        };
        path = path || '';

        if (entryWithTypeHint.entry.isFile) {
            (entryWithTypeHint.entry as FileSystemFileEntry).file(function (file: AugmentedFile) {
                file.relativePath = path;
                file.mimeTypeHint = entryWithTypeHint.mimeTypeHint;
                if (!file.type && !file.mimeTypeHint) {
                    ErrorReporter.capture('no mime type for file', {
                        level: 'warning',
                        extra: {
                            name: file.name,
                            type: file.type,
                            mimeTypeHint: file.mimeTypeHint,
                        },
                    });
                }
                dfd.resolve(file);
            }, errorHandler);
        } else if (entryWithTypeHint.entry.isDirectory) {
            const dirReader = (entryWithTypeHint.entry as FileSystemDirectoryEntry).createReader();
            readEntries(dirReader);
        } else {
            // return an empty list for file system items other than files or directories
            dfd.resolve([]);
        }
        return dfd.promise();
    }
}

export default NativeFileHandler;
